import React, { useEffect, useState } from "react"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import AuthCard from "../components/AuthCard"
import FormInput from "../components/FormInput"
import Button from "../components/Button"
import { useSnackbar } from "react-simple-snackbar"
import { errorSnackBar, successSnackBar } from "../components/snackBarOptions"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { login } from "../app/features/userSlice"
import requests from "../api/requests"

import { faEnvelope, faGlobe, faIdCard, faLock, faUser } from "@fortawesome/free-solid-svg-icons"
import { cpfMask, isCPFValid } from "../utils/cpf"

export default function SignUpScreen() {
  const [openErrorSnackbar] = useSnackbar(errorSnackBar)
  const [openSuccessSnackbar] = useSnackbar(successSnackBar)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const location = useLocation()
  const content = location?.state

  const [step, setStep] = useState(1)

  useEffect(() => {
    if (!content?.email) {
      navigate("/")
    }
  }, [navigate, content])

  const [values, setValues] = useState({
    email: content?.email,
    name: content?.name,
    internal_tax_id_country: "BR",
    internal_tax_id: "",
    password: "",
    confirmPassword: "",
  })

  const [isLoading, setIsLoading] = useState(false)

  const cpfInput = {
    id: 4,
    name: "internal_tax_id",
    type: "text",
    placeholder: "CPF",
    errorMessage: "Preencha com seu CPF válido",
    label: "CPF",
    required: true,
    autoFocus: true,
    icon: faIdCard,
    pattern: "\\d{3}\\.\\d{3}\\.\\d{3}-\\d{2}",
    inputMode: "numeric",
    autoComplete: "off",
  }

  const nifInput = {
    id: 4,
    name: "internal_tax_id",
    type: "text",
    placeholder: "NIF",
    errorMessage: "Preencha com seu NIF válido",
    label: "NIF",
    required: true,
    autoFocus: true,
    icon: faIdCard,
    pattern: "\\d{9}",
    inputMode: "numeric",
    autoComplete: "off",
  }

  const otherInput = {
    id: 4,
    name: "internal_tax_id",
    type: "text",
    placeholder: "Documento de Identificação",
    errorMessage: "Preencha com seu documento de identificação válido",
    label: "Documento",
    required: true,
    autoFocus: true,
    maxLength: 30,
    icon: faIdCard,
    autoComplete: "off",
  }

  const selectInput = () => {
    switch (values.internal_tax_id_country) {
      case "BR": return cpfInput
      case "PT": return nifInput
      default:   return otherInput
    }
  }

  // Step 1: personal data (name, country, tax id)
  const step1Inputs = [
    {
      id: 2,
      name: "name",
      type: "text",
      placeholder: "Nome completo",
      errorMessage: "O campo nome é obrigatório",
      label: "Nome",
      required: true,
      icon: faUser,
      autoComplete: "off",
    },
    {
      id: 3,
      name: "internal_tax_id_country",
      type: "select",
      placeholder: "País",
      options: [
        { value: "BR", label: "🇧🇷 Brasil" },
        { value: "PT", label: "🇵🇹 Portugal" },
        { value: "OTHER", label: "🌍 Outros" },
      ],
      errorMessage: "Selecione o país",
      label: "País",
      required: true,
      icon: faGlobe,
      autoComplete: "off",
    },
    { ...selectInput() },
  ]

  // Step 2: password
  const step2Inputs = [
    {
      id: 5,
      name: "password",
      type: "password",
      placeholder: "Senha",
      errorMessage: "Mínimo de 8 caracteres",
      label: "Senha",
      pattern: ".{8,}",
      required: true,
      icon: faLock,
      autoComplete: "off",
    },
    {
      id: 6,
      name: "confirmPassword",
      type: "password",
      placeholder: "Repita a senha",
      errorMessage: "As senhas não são iguais",
      label: "Repita a senha",
      pattern: values.password
        ? `^${values.password.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`
        : "",
      required: true,
      icon: faLock,
      autoComplete: "off",
    },
  ]

  const isAlumyMember =
    content?.source === "cosmo_alumy" || content?.isAlumyMember

  const validateStep1 = (e) => {
    e.preventDefault()
    const isValid = e.target.checkValidity()
    if (!isValid) {
      openErrorSnackbar("Preencha os campos solicitados")
      return
    }
    if (values.internal_tax_id_country === "BR" && !isCPFValid(values.internal_tax_id)) {
      openErrorSnackbar("Preencha com um CPF válido")
      return
    }
    setStep(2)
  }

  const register = async (e) => {
    e.preventDefault()
    const isValid = e.target.checkValidity()
    if (!isValid) {
      openErrorSnackbar("Preencha os campos solicitados")
      return
    }
    try {
      setIsLoading(true)
      const data = {
        name: values.name,
        email: values.email,
        password: values.password,
        confirmPassword: values.confirmPassword,
        internal_tax_id_country: values.internal_tax_id_country,
        internal_tax_id: values.internal_tax_id,
        isAlumyMember,
      }
      if (values.internal_tax_id_country === "BR") {
        data.internal_tax_id = values.internal_tax_id.replace(/\D/g, "")
      }
      const resp = await requests.register(data)
      dispatch(login(resp.data))
      navigate("/plan")
    } catch (error) {
      if (error?.response?.data?.message === 'user_blacklisted') {
        navigate('/blocked')
      } else {
        openErrorSnackbar("Email já cadastrado")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onChange = (e) => {
    if (e.target.name === "internal_tax_id") {
      switch (values.internal_tax_id_country) {
        case "BR":
          e.target.value = cpfMask(e.target.value)
          break
        case "PT":
          e.target.value = e.target.value.replace(/\D/g, "")
          break
        default:
          break
      }
    }
    if (e.target.name === "internal_tax_id_country") {
      setValues({ ...values, [e.target.name]: e.target.value, internal_tax_id: "" })
      return
    }
    setValues({ ...values, [e.target.name]: e.target.value })
  }

  const addToWaitlist = async (e) => {
    e.preventDefault()
    try {
      setIsLoading(true)
      await requests.addToWaitlist({ email: content?.email })
      openSuccessSnackbar("Email cadastrado na lista de espera")
    } catch {
      openErrorSnackbar("Erro ao cadastrar email na lista de espera")
    } finally {
      setIsLoading(false)
    }
  }

  const firstName = content?.name?.split(" ")[0]
  const formattedFirstName = firstName
    ? firstName.charAt(0).toUpperCase() + firstName.slice(1)
    : ""

  return (
    <AuthCard>
      {content?.acceptNewMembers ? (
        <>
          <div className="onboarding__progress">
            <div className={`onboarding__progress-dot${step >= 1 ? " onboarding__progress-dot--active" : ""}`} />
            <div className="onboarding__progress-line" />
            <div className={`onboarding__progress-dot${step >= 2 ? " onboarding__progress-dot--active" : ""}`} />
          </div>

          {step === 1 && (
            <>
              {content?.name ? (
                <>
                  <h1>Olá, {formattedFirstName}!</h1>
                  <h2>Preencha seus dados para criar sua conta</h2>
                </>
              ) : (
                <>
                  <h1>Criar conta</h1>
                  <h2>Preencha seus dados para continuar</h2>
                </>
              )}
              <form onSubmit={validateStep1} noValidate autoComplete="off">
                {step1Inputs.map((input) => (
                  <FormInput
                    key={input.id}
                    {...input}
                    value={values[input.name]}
                    onChange={onChange}
                  />
                ))}
                <div className="onboarding__actions">
                  <button
                    type="button"
                    className="btn btn--outline"
                    onClick={() => navigate("/")}
                  >
                    ← Voltar
                  </button>
                  <Button text="Continuar" />
                </div>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <h1>Crie sua senha</h1>
              <h2>Escolha uma senha segura para proteger sua conta</h2>
              <form onSubmit={register} noValidate autoComplete="off">
                {step2Inputs.map((input) => (
                  <FormInput
                    key={input.id}
                    {...input}
                    value={values[input.name]}
                    onChange={onChange}
                  />
                ))}
                <div className="onboarding__actions">
                  <button
                    type="button"
                    className="btn btn--outline"
                    onClick={() => setStep(1)}
                  >
                    ← Voltar
                  </button>
                  <Button text="Criar Conta" isLoading={isLoading} />
                </div>
              </form>
            </>
          )}
        </>
      ) : (
        <>
          <button className="onboarding__back" onClick={() => navigate("/")}>
            <ArrowBackIcon style={{ fontSize: "1rem" }} /> Voltar
          </button>
          <h1>Vagas Esgotadas</h1>
          <h2>
            As vagas estão esgotadas. Cadastre seu e-mail para ser avisado quando
            abrirem novas vagas.
          </h2>
          <form onSubmit={addToWaitlist} noValidate autoComplete="off">
            <FormInput
              key={1}
              id={1}
              name="email"
              type="email"
              placeholder="Email"
              errorMessage="Preencha com um email válido"
              label="Email"
              required
              disabled
              icon={faEnvelope}
              autoComplete="off"
              value={values.email}
              onChange={onChange}
            />
            <Button text="Quero ser avisado" isLoading={isLoading} />
          </form>
        </>
      )}
    </AuthCard>
  )
}
